import { useEffect, useRef, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  LogOut,
  Home,
  Headphones,
  CreditCard,
  BookOpen,
  FileText,
  FolderOpen,
  HelpCircle,
  ClipboardList,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { DynamicLogo } from '../DynamicLogo';
import { NotificationBell } from '../NotificationBell';
import { ThemeToggle } from '../ThemeToggle';

const sidebarSections = [
  {
    title: 'Principal',
    items: [
      { icon: Home, label: 'Dashboard', path: '/portal' },
    ],
  },
  {
    title: 'Serviços',
    items: [
      { icon: Headphones, label: 'Tickets', path: '/portal/tickets' },
      { icon: CreditCard, label: 'Faturas', path: '/portal/faturas' },
      { icon: FileText, label: 'Relatórios', path: '/portal/relatorios' },
      { icon: ClipboardList, label: 'Meus Briefings', path: '/portal/briefings' },
    ],
  },
  {
    title: 'Documentos',
    items: [
      { icon: FolderOpen, label: 'Documentos', path: '/portal/documentos' },
      { icon: BookOpen, label: 'Base de Conhecimento', path: '/portal/ajuda' },
    ],
  },
];

export function PortalLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    window.location.href = '/area-cliente';
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      {/* Vertical lines pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.2)_1px,transparent_1px)] bg-[size:8rem_100%] pointer-events-none" style={{ maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', maskComposite: 'intersect', WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', WebkitMaskComposite: 'source-in' }} />

      <div className="flex relative z-10">
        {/* Sidebar */}
        <aside className="w-64 min-h-screen bg-sidebar border-r border-sidebar-border text-sidebar-foreground flex flex-col">
          {/* Logo */}
          <div className="h-20 shrink-0 px-4 flex items-center">
            <DynamicLogo
              area="header"
              alt="Portal"
              className="h-8 w-auto"
              fallback={
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-brand rounded-md flex items-center justify-center">
                    <span className="text-white font-bold text-sm">P</span>
                  </div>
                  <span className="font-semibold">Portal</span>
                </div>
              }
            />
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 overflow-y-auto">
            {sidebarSections.map((section) => (
              <div key={section.title} className="mb-4">
                <p className="px-4 mb-2 text-xs font-medium text-sidebar-foreground/50 uppercase tracking-wider">
                  {section.title}
                </p>
                <div className="space-y-0.5 px-2">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                          isActive
                            ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium shadow-sm'
                            : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/80'
                        }`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="h-20 shrink-0 bg-sidebar border-b border-sidebar-border px-6 flex items-center justify-between sticky top-0 z-50">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium text-sidebar-foreground">Portal do Cliente</span>
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-2">
              {/* Ajuda */}
              <Link
                to="/portal/ajuda"
                aria-label="Ajuda"
                title="Central de Ajuda"
                className="p-2 rounded-lg hover:bg-sidebar-accent transition-colors"
              >
                <HelpCircle className="w-5 h-5 text-sidebar-foreground opacity-70" />
              </Link>

              {/* Tema claro/escuro */}
              <ThemeToggle variant="dropdown" />

              {/* Notification */}
              <NotificationBell />

              {/* Perfil */}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setIsProfileOpen((v) => !v)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-sidebar-accent transition-colors"
                  aria-label="Abrir menu do perfil"
                >
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-sidebar">{initials}</span>
                  </div>
                  <div className="text-left text-sm hidden sm:block">
                    <div className="font-medium text-sidebar-foreground leading-tight">{user?.name}</div>
                    <div className="text-sidebar-foreground opacity-70 text-xs leading-tight">{user?.email}</div>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-sidebar-foreground opacity-70 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                </button>

                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-border">
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                    <div className="divide-y divide-border">
                      <div className="flex items-center justify-between px-4 py-2.5">
                        <span className="text-sm text-muted-foreground">Tipo de Conta</span>
                        <span className="text-sm font-medium">Cliente</span>
                      </div>
                      <div className="flex items-center justify-between px-4 py-2.5">
                        <span className="text-sm text-muted-foreground">Status</span>
                        <span className="text-sm font-medium text-green-600 dark:text-green-500">Ativo</span>
                      </div>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors border-t border-border"
                    >
                      <LogOut className="w-4 h-4" />
                      Sair
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 overflow-y-auto overflow-x-hidden bg-muted/30 p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
